import { useState } from 'react';
import { modemApis } from '../../../../../../shared/apis/modem/apis';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Button, FileInput, Group, Modal } from '@mantine/core';
import { IconDownload, IconFileTypeXls, IconUpload } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { notifications } from '@mantine/notifications';
import { queries } from '../../../../../../shared/queries';
import { AxiosError } from 'axios';
import { AxiosCommonError } from '../../../../../../shared/types';
import { handleError } from '../../../../../ui/err/handleError';

export function ModemExcelRegModal(props: { opened: boolean; close: () => void; header: string }) {
  const { opened, close, header } = props;
  const [file, setFile] = useState<File | null>(null);
  const queryClient = useQueryClient();

  const { mutate: uploadExcel } = useMutation({
    mutationFn: (formData: FormData) => modemApis.uploadModemExcel(formData),
    onSuccess: () => {
      notifications.show({
        title: '업로드 성공',
        message: '엑셀 파일이 성공적으로 업로드되었습니다.',
        color: 'green',
      });
      queryClient.invalidateQueries(queries.modems.modemList._def);

      close();
    },
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  const uploadExcelTemplate = async () => {
    const formData = new FormData();
    formData.append('file', file as Blob);
    uploadExcel(formData);
  };

  const onClickClose = () => {
    close();
    setFile(null);
  };

  const getTemplateDownload = async () => {
    try {
      const res = await modemApis.getModemRegTemplateDownload();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '모뎀 등록 탬플릿.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: '엑셀 다운로드 실패',
        text: '엑셀 다운로드에 실패했습니다.',
        confirmButtonText: '확인',
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClickClose} title={header}>
      <Box>
        <FileInput
          clearable
          leftSection={<IconFileTypeXls />}
          label="엑셀 파일 업로드"
          placeholder="파일을 선택하세요."
          accept=".xlsx, .xls"
          leftSectionPointerEvents="none"
          value={file}
          onChange={setFile}
        />
        <Group justify="center" mt="lg">
          <Button
            variant="filled"
            color="secondary.5"
            leftSection={<IconUpload size={16} />}
            onClick={() => {
              uploadExcelTemplate();
            }}
          >
            파일 업로드
          </Button>
          <Button
            variant="outline"
            color="primary.5"
            leftSection={<IconDownload size={16} />}
            onClick={() => {
              getTemplateDownload();
            }}
          >
            탬플릿 다운로드
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
